import _memoize from 'lodash/memoize';
import { PRIMARY, TERTIARY, makeThemeColor } from '@tornado-rdk/theme';
import { HD, FULL_HD } from '@bedrock-rdk/constants';
import { getFont, FONT } from '@tornado-rdk/typography';
import { middle } from '../utils/sizes';
import { makeDefaultStylingContext } from '../utils/style';
import {
  BORDER_WIDTH,
  SCALE_FOCUSED,
  BORDER_COLOR,
  INNER_BORDER_COLOR,
  FOCUSED_BORDER_ALPHA,
  UNFOCUSED_BORDER_ALPHA,
} from '../utils/focus';
import { TRANSPARENT } from '../utils/colors';

export const defaultContext = makeDefaultStylingContext();

export const MOSAIC_CARD_SIZE = {
  [HD]: {
    w: 199,
    h: 112,
  },
  [FULL_HD]: {
    w: 300,
    h: 169,
  },
};

export const PADDING = 8;

export const colors = {
  background: makeThemeColor(TERTIARY, 0.3),
  icon: makeThemeColor(PRIMARY),
  label: makeThemeColor(PRIMARY),
};

export const ICON_SIZES = {
  [HD]: Math.round(MOSAIC_CARD_SIZE[HD].h / 2),
  [FULL_HD]: Math.round(MOSAIC_CARD_SIZE[FULL_HD].h / 2),
};

export const BORDER_RADIUS = {
  [HD]: 4,
  [FULL_HD]: 4,
};

export const getIconViewBox = _memoize(screenSize => ({
  h: ICON_SIZES[screenSize],
}));

export const getInitialStyle = ({ screenSize, colorMode } = defaultContext) => {
  const { w, h } = MOSAIC_CARD_SIZE[screenSize];
  const borderSize = BORDER_WIDTH[screenSize];
  const borderOffset = borderSize * 2;

  return {
    Border: {
      alpha: 0,
      w: w + borderOffset,
      h: h + borderOffset,
      x: -borderSize,
      y: -borderSize,
      color: TRANSPARENT,
      shader: {
        radius: BORDER_RADIUS[screenSize],
        outline: BORDER_WIDTH[screenSize],
        strokeColor: TRANSPARENT,
        innerColor: TRANSPARENT,
      },
    },
    Background: {
      w,
      h,
      shader: {
        fillColor: colors.background[colorMode],
        radius: BORDER_RADIUS[screenSize],
      },
    },
    Image: {
      w,
      h,
      shader: {
        radius: BORDER_RADIUS[screenSize],
      },
    },
    Content: {
      w,
      h,
      flex: {
        direction: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      },
      IconWrapper: {
        w,
        h: ICON_SIZES[screenSize],
        Icon: {
          mountX: 0.5,
          x: middle,
          color: colors.icon[colorMode],
        },
      },
      Label: {
        color: colors.label[colorMode],
        text: {
          ...getFont(FONT.BERLIN_2, screenSize),
          wordWrapWidth: w - PADDING * 2,
          wordWrap: true,
          wordBreak: true,
          advancedRenderer: true,
          textAlign: 'center',
          verticalAlign: 'bottom',
          maxLines: 2,
          maxLinesSuffix: '...',
        },
      },
    },
  };
};

export const getFocusedStyle = ({ colorMode }) => ({
  Border: {
    smooth: {
      alpha: FOCUSED_BORDER_ALPHA,
      scale: SCALE_FOCUSED,
      'shader.strokeColor': BORDER_COLOR[colorMode],
      'shader.innerColor': INNER_BORDER_COLOR[colorMode],
      'shader.scale': SCALE_FOCUSED,
    },
  },
  Background: {
    smooth: {
      scale: SCALE_FOCUSED,
      'shader.scale': SCALE_FOCUSED,
    },
  },
  Image: {
    smooth: {
      scale: SCALE_FOCUSED,
      'shader.scale': SCALE_FOCUSED,
    },
  },
});

export const unFocusedStyle = {
  Border: {
    smooth: {
      alpha: UNFOCUSED_BORDER_ALPHA,
      scale: 1,
      'shader.strokeColor': TRANSPARENT,
      'shader.innerColor': TRANSPARENT,
      'shader.scale': 1.0,
    },
  },
  Background: {
    smooth: {
      scale: 1,
      'shader.scale': 1.0,
    },
  },
  Image: {
    smooth: {
      scale: 1,
      'shader.scale': 1.0,
    },
  },
};
